// EDEN · section numbering + auto-summed marks (Module P2b, Job 3). blocks.ts's own header
// deliberately keeps every block type self-contained ("test-editor.ts never special-cases a type
// by name") — but a `section` block's Roman/letter label and its marks total are properties of the
// WHOLE block list (everything beneath it, until the next section), not of its own content, so they
// cannot live inside blocks.ts's per-block BlockDef. This is the one legitimate exception: a small,
// list-level layout pass, called once by test-print.ts and test-editor.ts, never duplicated inline.
//
// Rules (owner brief, EDEN_ClaudeCode_Prompts_P2b_P2c_2026-09-04.md, Job 3):
//   - A `section` at level 1 gets the next Roman numeral (I, II, III…); level 2 gets the next
//     letter (A, B, C…) SINCE THE LAST level-1 section (so a fresh "I." resets the letter run).
//   - A section's marks = the sum of every non-instructions, non-section block's marks between it
//     and the next section whose level is <= its own (i.e. a sub-section stops at the next
//     sub-section OR section; a section stops only at the next section, absorbing any sub-sections
//     and their contents in between). Never typed — see assessments/[id].ts forcing section marks
//     to 0 in storage.
//   - Question numbers restart at 1 at every section AND every sub-section boundary.

import type { BlockType, EditorBlock } from "./blocks";

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII", "XIII", "XIV", "XV"];

export function sectionLevel(content: Record<string, unknown>): 1 | 2 {
  return content.level === 2 ? 2 : 1;
}

export interface BlockLayout {
  /** Set only for a `section` block — "I." / "A." etc. */
  sectionLabel?: string;
  sectionLevel?: 1 | 2;
  /** Set only for a `section` block — the auto-summed marks of everything beneath it. */
  sectionMarks?: number;
  /** Set for every gradable, non-section block — the question number, restarting per (sub-)section. */
  questionNo?: number;
}

const NUMBERED_TYPES = new Set<BlockType>([
  "reading_passage", "mcq", "gap_fill", "short_answer", "writing", "listening", "picture",
]);
// `matching` and `spelling` print their own complete internal numbering (a lettered/numbered table
// or a numbered word list) — an outer question number in front of the whole block would double up
// against it, which the reference papers never show (EN10 p1: "B. Match the words…" carries no
// leading digit of its own, only the table's own 1/2/3…).

function marksUnderSection(blocks: EditorBlock[], startIdx: number, level: 1 | 2): number {
  let sum = 0;
  for (let i = startIdx + 1; i < blocks.length; i++) {
    const b = blocks[i]!;
    if (b.type === "section") {
      if (sectionLevel(b.content) <= level) break;
      continue; // a nested sub-section's own heading carries no marks; its contents do (below)
    }
    if (b.type !== "instructions") sum += b.marks || 0;
  }
  return sum;
}

export function layoutBlocks(blocks: EditorBlock[]): BlockLayout[] {
  const out: BlockLayout[] = [];
  let romanIdx = 0;
  let subIdx = 0;
  let qNo = 0;

  blocks.forEach((b, i) => {
    if (b.type === "section") {
      const level = sectionLevel(b.content);
      if (level === 1) { romanIdx++; subIdx = 0; }
      else subIdx++;
      qNo = 0;
      out.push({
        sectionLabel: level === 1 ? `${ROMAN[romanIdx - 1] ?? romanIdx}.` : `${String.fromCharCode(64 + subIdx)}.`,
        sectionLevel: level,
        sectionMarks: marksUnderSection(blocks, i, level),
      });
      return;
    }
    if (b.type === "instructions" || !NUMBERED_TYPES.has(b.type)) {
      out.push({});
      return;
    }
    qNo++;
    out.push({ questionNo: qNo });
  });

  return out;
}
